import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { modulesApi, ModuleDetail } from '@/api/modules';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Package,
  School,
  DollarSign,
  Calendar,
} from 'lucide-react';

export function ModuleDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [module, setModule] = useState<ModuleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (code) {
      fetchModule(code);
    }
  }, [code]);

  const fetchModule = async (moduleCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await modulesApi.getByCode(moduleCode);
      setModule(data);
    } catch (err) {
      setError('Erreur lors du chargement du module');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };



  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/modules')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux modules
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Module non trouve'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/modules">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                {module.name}
              </h1>
              <p className="text-sm text-muted-foreground font-mono">{module.code}</p>
            </div>
          </div>
        </div>

      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Module Info */}
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-primary" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Code</dt>
              <dd className="mt-1 font-mono text-sm font-semibold">{module.code}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Nom</dt>
              <dd className="mt-1 font-semibold">{module.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Nombre d'écoles
              </dt>
              <dd className="mt-1">
                <Badge variant="secondary" className="font-mono">
                  {module.schoolCount}
                </Badge>
              </dd>
            </div>
          </CardContent>
        </Card>

        {/* Pricing by School Year */}
        <Card className="md:col-span-2">
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-primary" />
              Tarification par année fiscale
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {module.pricing.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Aucune tarification definie
              </p>
            ) : (
              <div className="space-y-4">
                {module.pricing.map((pricing) => (
                  <div
                    key={pricing.schoolYearId}
                    className="border rounded-lg p-4 bg-muted/20"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{pricing.schoolYearCode}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <dt className="text-muted-foreground">Tranche -100</dt>
                        <dd className="font-medium">{formatPrice(pricing.price)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Tranche 100-250</dt>
                        <dd className="font-medium">{formatPrice(pricing.priceT2)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Tranche 250-500</dt>
                        <dd className="font-medium">{formatPrice(pricing.priceT3)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Tranche +500</dt>
                        <dd className="font-medium">{formatPrice(pricing.priceT4)}</dd>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Schools using this module */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <School className="h-4 w-4 text-primary" />
            Écoles utilisant ce module ({module.schools.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {module.schools.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Aucune école n'utilise ce module pour le moment
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {module.schools.map((school) => (
                <div
                  key={school.id}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors"
                >
                  <School className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{school.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {school.code}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
